/**
 * @file Dynamic field inline and capture modal rendering.
 */

import { appData } from "../../app/state/state.js";
import {
    applyBrandingVariables,
} from "../ui/brandingTokens.js";
import {
    formatDate,
} from "../utils/dateUtils.js";
import {
    focusFirstRegisteredControlError,
    validateRegisteredControls,
} from "../ui/controls/controlCore.js";
import {
    activateFormModalAccessibility,
    clearModalCloseCallback,
    closeForm,
    formModalElement,
    setModalCloseCallback,
} from './modalShell.js';
import {
    bindNativeFieldBehavior,
    validateNativeFormFields,
} from "./nativeFieldStyles.js";
import {
    bindDatePlaceholderColor,
    getDynamicFieldHtml,
    mountDynamicSelectControls,
} from './dynamicFields.js';

/**
 * Renders inline dynamic fields for a selected item type.
 * @param {object} typeConfig Type configuration containing fields.
 * @param {HTMLFormElement} itemForm Form receiving the dynamic field block.
 * @returns {void}
 */
export function renderDynamicFieldForm(typeConfig, itemForm) {
    const anchor = itemForm.querySelector('#dynamic-fields-anchor');
    if (!anchor || !typeConfig?.fields || typeConfig.fields.length === 0) {return;}

    // Check if container already exists (avoid duplicates) - essential for multi-select
    if (document.getElementById(`dynamic-fields-${typeConfig.id}`)) {return;}

    const container = document.createElement('div');
    container.id = `dynamic-fields-${typeConfig.id}`;
    // User Request: Slightly darker blue background, Title Case header
    container.className = 'md:col-span-2 p-4 bg-blue-100/40 dark:bg-blue-900/20 rounded-lg animate-fade-in';

    const itemType = itemForm.dataset.type || '';
    const itemTypeDisplay = itemType ? itemType.charAt(0).toUpperCase() + itemType.slice(1).replace(/_/g, ' ') : '';
    const headerText = `${typeConfig.name} ${itemTypeDisplay} Specifics`;

    container.innerHTML = `
        <h5 class="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3">${headerText}</h5>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-3">
            ${typeConfig.fields.map(field => getDynamicFieldHtml(field)).join('')}
        </div>
    `;

    anchor.appendChild(container);

    const dynamicDates = container.querySelectorAll('input[type="date"], input[type="datetime-local"]');
    dynamicDates.forEach(input => bindDatePlaceholderColor(input));
    bindNativeFieldBehavior(container);
    mountDynamicSelectControls(container, typeConfig);
}

/**
 * Opens the dynamic field capture modal used from detail edit flows.
 * @param {object} typeConfig Type configuration containing fields.
 * @param {string} itemType Parent item type.
 * @param {Function} onSave Callback receiving generated markdown.
 * @param {Function} onCancel Callback for cancel.
 * @param {Function} onClose Callback for modal close.
 * @returns {void}
 */
export function openDynamicFieldsModal(typeConfig, itemType, onSave, onCancel, onClose) {
    if (!formModalElement || !typeConfig?.fields || typeConfig.fields.length === 0) {return;}

    // Set the global close callback
    setModalCloseCallback(onClose);

    const formTitle = formModalElement.querySelector('#form-title');
    const formHeaderActions = formModalElement.querySelector('#form-header-actions');
    const formFields = formModalElement.querySelector('#form-fields');
    const itemForm = formModalElement.querySelector('#item-form');
    const headerRow = formModalElement.querySelector('#form-header-row');
    applyBrandingVariables(formModalElement, appData);
    if (formHeaderActions) {formHeaderActions.innerHTML = '';}

    // Hide standard buttons, we will use custom ones
    const submitBtn = formModalElement.querySelector('button[type="submit"]');
    const cancelBtn = document.getElementById('cancel-form-btn');
    const footer = cancelBtn?.parentElement || submitBtn?.parentElement || null;
    if (submitBtn) {submitBtn.style.display = 'none';}
    if (cancelBtn) {cancelBtn.style.display = 'none';}
    if (footer) {footer.style.display = 'none';}

    // Title
    const _itemTypeDisplay = itemType ? itemType.charAt(0).toUpperCase() + itemType.slice(1).replace(/_/g, ' ') : 'Item';
    formTitle.textContent = `Add ${typeConfig.name} Details`; // e.g. "Add Clinical Note Details"

    itemForm.dataset.type = 'dynamic_fields_capture'; // generic type
    if (headerRow) {headerRow.classList.replace('mb-6', 'mb-3');}

    formFields.innerHTML = `
        <div class="rounded-lg border border-blue-100 bg-blue-100/40 p-4 dark:border-blue-900/30 dark:bg-blue-900/20">
            <div class="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-4" id="dynamic-modal-fields-container">
                ${typeConfig.fields.map(field => getDynamicFieldHtml(field)).join('')}
            </div>
        </div>
        <div class="mt-4 flex justify-end gap-3">
            <button type="button" id="btn-dyn-cancel" class="inline-flex h-9 items-center justify-center rounded-md bg-gray-200 px-4 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-300 focus:outline-none">Cancel</button>
            <button type="button" id="btn-dyn-save" class="inline-flex h-9 items-center justify-center rounded-md bg-[var(--dfc-brand-primary)] px-4 text-sm font-semibold text-gray-900 transition-colors hover:bg-[var(--dfc-brand-primary-hover)] focus:outline-none">Save Details</button>
        </div>
    `;

    const container = document.getElementById('dynamic-modal-fields-container');

    container.querySelectorAll('input[type="date"], input[type="datetime-local"]').forEach(input => {
        bindDatePlaceholderColor(input);
    });

    bindNativeFieldBehavior(container);
    mountDynamicSelectControls(container, typeConfig);

    // Handle Cancel
    const btnCancel = document.getElementById('btn-dyn-cancel');
    if (btnCancel) {
        btnCancel.onclick = () => {
            closeForm();
            if (onCancel) {onCancel();}
        };
    }

    // Handle Save
    const btnSave = document.getElementById('btn-dyn-save');
    if (btnSave) {
        btnSave.onclick = () => {
            const nativeValid = validateNativeFormFields(container);
            const registeredValidation = validateRegisteredControls(container);
            if (!nativeValid || !registeredValidation.valid) {
                if (nativeValid) {
                    focusFirstRegisteredControlError(registeredValidation);
                }
                return;
            }

            // Generate Markdown
            const markdownParts = [];
            // Mirrors modal submit text-field formatting for dynamic captures.
            typeConfig.fields.forEach(field => {
                const name = field.name || `custom_${field.label.replace(/\s+/g, '_').toLowerCase()}`;
                const input = container.querySelector(`[name="${name}"]`);
                if (!input) {return;}

                let value;
                if (input.type === 'checkbox') {
                    value = input.checked ? 'Yes' : 'No';
                } else {
                    value = input.value;
                }

                // Format dates as DD/MM/YYYY
                if ((input.type === 'date' || input.type === 'datetime-local') && value) {
                    value = formatDate(value);
                }

                if (value) {
                    // Format:
                    //   **Label**: First line
                    //   Second Line ...
                    const lines = value.split('\n');
                    // Key Line:   **Label**: First Line
                    markdownParts.push(`  **${field.label}**: ${lines[0]}`);
                    // Subsequent Lines:   Line content (indented to align)
                    for (let i = 1; i < lines.length; i++) {
                        markdownParts.push(`  ${lines[i]}`);
                    }
                }
            });

            const markdownText = markdownParts.length > 0
                ? `\n**${typeConfig.name} Details**\n` + markdownParts.join('\n')
                : '';

            // Clear the close callback before closeForm() runs, since onSave handles the dropdown reset
            clearModalCloseCallback();

            closeForm();
            if (onSave) {onSave(markdownText);}
        };
    }

    formModalElement.classList.add('flex');
    activateFormModalAccessibility(
        formModalElement.querySelector("input:not([disabled]), textarea:not([disabled])"),
    );
}
